<template>
  <v-card variant="outlined" class="dashboard-card">
    <v-toolbar flat class="dashboard-card__toolbar">
      <v-btn icon class="dashboard-card__toggle" @click="toggleCard">
        <v-icon color="rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity))">
          {{ showContent ? 'mdi-chevron-down-box' : 'mdi-chevron-right-box' }}
        </v-icon>
      </v-btn>
      <v-toolbar-title>
        <span>{{ title }}&nbsp;</span>
        <span v-if="!loading" class="ml-1 overline">({{ count }})</span>
        <template v-else>
          <span class="ml-1 overline">
            <v-progress-circular
              indeterminate
              color="primary"
              size="15"
              width="1"
              class="mb-1 mx-1"
            />
          </span>
        </template>
      </v-toolbar-title>
      <!-- The toolbar's own controls belong to an open card, so they fade with
           it rather than blinking out on the first frame of the collapse. Each
           needs a single element of its own: a transition cannot take a slot,
           which may render any number of nodes. -->
      <v-fade-transition :disabled="!animate">
        <div v-if="showContent" class="dashboard-card__title-append d-flex align-center">
          <slot name="title-append" />
        </div>
      </v-fade-transition>
      <v-spacer />
      <v-fade-transition :disabled="!animate">
        <div v-if="showContent" class="dashboard-card__actions d-flex align-center">
          <slot name="actions" />
        </div>
      </v-fade-transition>
    </v-toolbar>
    <!-- The content stays mounted behind `v-show`, so the expand animates a
         subtree the browser has already laid out. The padding belongs to the
         inner element: on the collapsed frame an animated box keeps its own
         padding, which reads as the content jumping to its last 32px rather
         than closing. (Vuetify's expansion panel splits the two the same way.) -->
    <v-expand-transition :disabled="!animate">
      <div v-show="showContent" class="dashboard-card__content">
        <v-card-text>
          <slot name="content" />
        </v-card-text>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { debounce } from 'lodash-es';

import { UserWidgetsEnum } from '@/bundles/Settings/enums/userLayoutEnum';

export default defineComponent({
  name: 'DashboardCard',

  props: {
    title: {
      type: String,
      required: true,
    },
    count: {
      type: Number,
      default: 0,
    },
    widgetType: {
      type: String as PropType<UserWidgetsEnum>,
      required: true,
    },
    loading: Boolean,
    expanded: Boolean,
  },

  emits: ['update:expanded'],

  data (): { showContent: boolean; animate: boolean } {
    return {
      showContent: this.expanded,
      // The saved layout decides which cards start open, and the first card to
      // arrive collapsed would otherwise play its collapse on load. Only a
      // click turns the animations on.
      animate: false,
    };
  },

  watch: {
    expanded (value: boolean) {
      this.showContent = value;
    },
  },

  methods: {
    toggleCard () {
      this.animate = true;
      this.showContent = !this.showContent;
      this.saveCardStateDebounced(this);
    },
    saveCardStateDebounced: debounce(function (vm) {
      vm.$emit('update:expanded', {
        name: vm.widgetType,
        value: vm.showContent
      });
    }, 400),
  }
});
</script>

<style lang="scss" scoped>
// The toggle icon sits as far from the card's left edge as from its top and bottom (21px).
// The parity rule pulls a toolbar's first icon button 12px out; 8px keeps the icon at 21px.
.v-application .dashboard-card__toolbar .v-btn.v-btn--icon.dashboard-card__toggle {
  margin-inline-start: -8px;
}

// Each card's main table sits in its own outline, like the tables in record
// cards. A table already inside an outlined card of its own (the Tasks card's
// panels, an expanded invoice list in a row) is left alone, or it would get a
// second border. BaseTable (New Availabilities, Recent Comps) draws its
// pagination bar as a sibling of the table, so the outline goes on its wrapper
// to take the pagination in, and the table inside gets none.
.dashboard-card__content :deep(.v-table:not(.dashboard-card__content .v-card--variant-outlined .v-table):not(.data-list-wrapper .v-table)),
.dashboard-card__content :deep(.data-list-wrapper) {
  border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  overflow: hidden;
}
</style>
